import { useState } from 'react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Button } from '@/components/ds2'
import { PanelDrawer } from './PanelDrawer'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { showError, showSuccess } from '@/utils/snackbar'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { useInvoicingChat } from '../../invoicing/chatBridge'
import { useFactoringBatchableStore } from '../../invoicing/factoringBatchableStore'
import { formatCount, formatUsd } from '../results/format'
import { FACTORING_BATCH_MIN_SENTENCE, plainBatchRefusalOf } from '../results/refusalWords'
import { amountOf, invoiceBlockOf, rowKeyOf } from './invoicingRows'
import { BATCH_SIZE, batchesOf, factoringEligibilityOf, prepareFactoringBatches, preparedNoteOf } from './factoringBatch'

interface FactoringBatchDrawerProps {
    open: boolean
    /** The provider's display name (the connected factoring provider). */
    provider: string
    /** The rows to batch, in table order. A blocked one stays listed with its reason and a Remove link. */
    rows: TmsInvoiceRow[]
    onClose: () => void
    onRemove: (key: string) => void
    onDone: () => void
}

/** Re-reads the rows and the thread after the batches are prepared. A failed read is shown; it never undoes what was prepared. */
const refreshAfterPrepare = async (refreshThread: () => Promise<void>, tabId: string, rows: TmsInvoiceRow[]): Promise<void> => {
    try {
        await useInvoicingRowsStore.getState().refresh(
            tabId,
            rows.map((row) => row.load_numbers[0]).filter((n): n is string => Boolean(n))
        )
        await refreshThread()
        // The prepared invoices left the company's batchable set: the factoring offers count without them.
        await useFactoringBatchableStore.getState().refresh(tabId, true)
    } catch (failure) {
        showError(getUserErrorMessage(failure))
    }
}

/**
 * Review selected factoring batch (v17 flow 14): the eligible loads with their totals, the blocked ones with Remove, and the button that
 * prepares the batches. Nothing is sent to the factor from here: submitting stays the chat's approval card.
 */
export const FactoringBatchDrawer = ({ open, provider, rows, onClose, onRemove, onDone }: FactoringBatchDrawerProps) => {
    const [busy, setBusy] = useState(false)
    const chat = useInvoicingChat()

    if (!open) return null

    const blocked = rows.filter((row) => invoiceBlockOf(row) !== null)
    const { eligible, batchable } = factoringEligibilityOf(rows)
    const eligibleTotal = eligible.reduce((sum, row) => sum + amountOf(row), 0)
    const batches = batchesOf(eligible).length

    const prepare = async () => {
        setBusy(true)
        try {
            const tabId = await chat.ensureTab()
            const outcomes = await prepareFactoringBatches(tabId, eligible)
            // The batches are prepared: report them before any read that can fail, so a failed refresh never hides what was done.
            const failed = outcomes.filter((outcome) => !outcome.prepared)
            for (const outcome of outcomes) if (outcome.prepared) chat.appendAgentNote(...preparedNoteOf(outcome, provider))
            if (failed.length === 0) showSuccess('Factoring batch prepared.')
            else if (outcomes.length === 1) showError(plainBatchRefusalOf(failed[0].message))
            else showError(`${failed.length} of ${outcomes.length} batches were not prepared. ${plainBatchRefusalOf(failed[0].message)}`)
            onDone()
            await refreshAfterPrepare(chat.refreshThread, tabId, eligible)
        } catch (failure) {
            showError(getUserErrorMessage(failure))
        } finally {
            setBusy(false)
        }
    }

    return (
        <PanelDrawer
            open={open}
            onClose={onClose}
            title="Review selected factoring batch"
            subtitle={
                <>
                    {provider} · {formatCount(eligible.length)} eligible {eligible.length === 1 ? 'load' : 'loads'}
                </>
            }
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Back
                    </Button>
                    <Button onClick={() => void prepare()} disabled={blocked.length > 0 || !batchable || busy} loading={busy}>
                        Approve & prepare batch
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-sm">
                {rows.map((row) => {
                    const block = invoiceBlockOf(row)
                    const number = row.load_numbers[0] ?? ''
                    return (
                        <div key={rowKeyOf(row)} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-foreground">
                                    #{number} · {row.broker ?? '—'}
                                </div>
                                {block ? (
                                    <div className="mt-1 flex items-center gap-2 text-xs text-warning-soft-foreground">
                                        <span>{block}</span>
                                        <button type="button" className="underline" onClick={() => onRemove(rowKeyOf(row))}>
                                            Remove
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mt-1 text-xs text-foreground-muted">
                                        {row.invoice_number ? `Existing draft ${row.invoice_number}` : 'Invoice draft will be created on approval'}
                                    </div>
                                )}
                            </div>
                            <strong className="tabular-nums">{formatUsd(amountOf(row))}</strong>
                        </div>
                    )
                })}
                <div className="flex items-center justify-between font-semibold">
                    <span>Eligible total</span>
                    <span className="tabular-nums">{formatUsd(eligibleTotal)}</span>
                </div>
                {!batchable && rows.length > 0 && (
                    <p role="status" className="rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
                        {FACTORING_BATCH_MIN_SENTENCE} Select another invoice to continue.
                    </p>
                )}
                {batches > 1 && (
                    <p className="text-xs text-foreground-muted">
                        Batches hold up to {BATCH_SIZE} invoices: this prepares {batches} batches.
                    </p>
                )}
                <p className="rounded-lg bg-surface-secondary px-3 py-2 text-xs text-foreground-muted">Prepared batches are not sent to the factor.</p>
            </div>
        </PanelDrawer>
    )
}
